import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import { Plus, FileText, Building2 } from 'lucide-react'
import Button from 'components/Button'
import Popup from 'components/Popup'
import Placeholder from 'components/Placeholder'
import Spinner from 'components/Spinner'
import LocationTenantEdit from '../components/LocationTenantEdit'
import NumberFormat from 'react-number-format'
import AltRetailerLogo from 'images/Old_Navy_Logo.png'
import { Avatar, AvatarImage, AvatarFallback } from 'components/ui/avatar'

import { uploadCenterTenants } from 'redux/actions/centerTenantActions'
import Layout from 'components/Layout/Layout'
import ListAction from 'features/Actions/components/ListAction'
import EmptyLogo from 'images/empty_folder.png'
import {
	createLocation,
	deleteLocation,
	getCenterTenants,
	updateLocation,
} from 'slices/locationSlice'
import { useParams } from 'react-router-dom'
import notify from 'components/ui/notify'
import {
	Table,
	TableHeader,
	TableBody,
	TableHead,
	TableRow,
	TableCell,
} from 'components/ui/table'

const headerCells = [
	{ id: 'Image', label: '', sortable: false },
	{ id: 'tenant', label: 'Tenant', sortable: true },
	{ id: 'tenantSize', label: 'Sq. Ft.', sortable: true },
	{ id: 'estimatedSales', label: 'Estimated Sales', sortable: true },
]

const LocationTenants = ({ userId, location }) => {
	const dispatch = useDispatch()

	const navigate = useNavigate()

	const { id: locationId } = useParams()
	// const locationId = location._id
	const ownerId = location.owner._id

	const initialTenant = {
		_id: 0,
		isTenantOnly: true,
	}

	const [openPopup, setOpenPopup] = useState(false)
	const [popupTitle, setPopupTitle] = useState('Update Tenant')
	const [recordForEdit, setRecordForEdit] = useState(null)

	const {
		centerTenantEntities,
		centerTenantIds,
		centerTenantsLoading,
		centerTenantsInitialized,
	} = useSelector(state => state.locations)
	const centerTenants = centerTenantIds.map(id => centerTenantEntities[id])

	useEffect(() => {
		// if (!centerTenantsInitialized) {
		dispatch(getCenterTenants(locationId))
		// }
	}, [centerTenantsInitialized, dispatch, locationId])

	const addDropTenantsHandler = () => {
		if (userId === ownerId) {
			navigate(`/locations/${locationId}/addtenants`)
		} else {
			notify({
				title: `Add Tenant Error!`,
				text: `You must be the center originator of ${location.assetName} to add a new location.`,
				icon: 'error',
				button: 'Ok',
			})
		}
	}

	const uploadCenterTenantsHandler = result => {
		const newTenants = [...result.data]
		let tenants = []

		for (let i = 0, len = newTenants.length; i < len; i++) {
			tenants.push({
				tenant: newTenants[i].tenant,
				userid: userId,
				parentid: locationId,
			})
		}

		dispatch(uploadCenterTenants(locationId, tenants))
	}

	const addEditTenant = async (newTenant, resetForm) => {
		const assetName = newTenant.assetName
		const centerLocation = newTenant.centerLocation
		const sellingSqft = newTenant.totalSqft
		const totalSqft = newTenant.totalSqft
		const estimatedSales = newTenant.estimatedSales
		const retailer = newTenant.retailer
		const address = newTenant.address
		const city = newTenant.city
		const stateProvince = newTenant.stateProvince
		const postalCode = newTenant.postalCode
		const country = newTenant.country
		const latitude = newTenant.latitude
		const longitude = newTenant.longitude
		const partners = newTenant.partners
		const image = newTenant.image
		const isTenantOnly = newTenant.isTenantOnly

		if (newTenant._id === 0) {
			dispatch(
				createLocation({
					owner: userId,
					parentid: locationId,
					assetType: 'location',
					locationType: 'Retail Location',
					...newTenant,
				}),
			)
		} else {
			dispatch(
				updateLocation({
					_id: newTenant._id,
					owner: userId,
					parentid: locationId,
					assetName,
					assetType: 'location',
					locationType: 'Retail Location',
					retailer,
					centerLocation,
					sellingSqft,
					totalSqft,
					estimatedSales,
					address,
					city,
					stateProvince,
					postalCode,
					country,
					latitude,
					longitude,
					image,
					partners,
					isTenantOnly,
				}),
			)
		}

		resetForm()
		setOpenPopup(false)
		setRecordForEdit(null)
	}

	const editTenantHandler = id => {
		if (id.isTenantOnly) {
			setPopupTitle('Update Tenant')
			openInPopup(id)
		} else {
			navigate(`/locations/${id._id}/edit`)
		}
	}

	const openInPopup = currentTenent => {
		setRecordForEdit(currentTenent)
		setOpenPopup(true)
	}

	const newTenatHandler = () => {
		setPopupTitle('New Tenant')
		setRecordForEdit(initialTenant)
		setOpenPopup(true)
	}

	// if (tenantLoading) return <Spinner containerHeight="50vh" />

	const hasTenants = centerTenants.length > 0

	return (
		<div className="space-y-4 p-2">
			<div className="grid grid-cols-12 gap-6">
				{hasTenants ? (
					<div className="col-span-9">
						<Table>
							<TableHeader>
								<TableRow className="border-b-2 border-[#027fff] hover:bg-transparent">
									{headerCells.map(cell => (
										<TableHead
											key={cell.id}
											className="text-gray-700 text-xs font-semibold whitespace-nowrap"
										>
											{cell.label}
										</TableHead>
									))}
								</TableRow>
							</TableHeader>
							<TableBody>
								{centerTenants.map(tenant => (
									<TableRow
										key={tenant._id}
										className="h-7 hover:bg-[#fffbf2] cursor-pointer"
										onClick={() => editTenantHandler(tenant)}
									>
										<TableCell className="py-1 text-sm">
											<Avatar><AvatarImage src={tenant.image} /><AvatarFallback><img src={AltRetailerLogo} className="h-full w-full object-cover" /></AvatarFallback></Avatar>
										</TableCell>
										<TableCell className="py-1 text-sm">
											{tenant.assetName}
										</TableCell>
										<TableCell className="py-1 text-sm">
											<NumberFormat
												className="feed__fileSize"
												value={tenant.totalSqft}
												decimalScale={0}
												displayType={'text'}
												thousandSeparator={true}
											/>
										</TableCell>
										<TableCell className="py-1 text-sm">
											<NumberFormat
												className="feed__fileSize"
												value={tenant.estimatedSales}
												decimalScale={0}
												displayType={'text'}
												thousandSeparator={true}
											/>
										</TableCell>
									</TableRow>
								))}
							</TableBody>
						</Table>
					</div>
				) : (
					<div className="col-span-9">
						<Placeholder aspectRatio="16:9" isFlexColumn isTransparent>
							<h1 className="h1">We don't have any tenants, yet.</h1>
							<div>
								<img className="details__avatar" src={EmptyLogo} alt="Avatar" />
							</div>
							<div>
								<Button onClick={newTenatHandler} variant="success" isLarge>
									<Plus className="w-4 h-4" /> Start Here to Add a New Tenant
								</Button>
							</div>
						</Placeholder>
					</div>
				)}
				<div className="col-span-3">
					<Layout.SidebarItem title="Actions" hasNoMaxHeight>
						<ul>
							<li className="actionList__item">
								<ListAction
									handleAction={addDropTenantsHandler}
									actionLabel={'Add/Remove Tenants'}
									actionIcon={<Plus className="w-4 h-4" />}
								/>
							</li>
							<li className="actionList__item">
								<ListAction
									handleAction={uploadCenterTenantsHandler}
									actionLabel={'Upload Tenant List'}
									actionIcon={<Plus className="w-4 h-4" />}
								/>
							</li>
						</ul>
					</Layout.SidebarItem>
				</div>
			</div>

			<Popup title={popupTitle} openPopup={openPopup} setOpenPopup={setOpenPopup} icon={<Building2 className="w-5 h-5" />} description="Manage location tenants">
				<LocationTenantEdit recordForEdit={recordForEdit} addEditTenant={addEditTenant} />
			</Popup>
		</div>
	)
}

export default LocationTenants
